import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { FileText, Download, ExternalLink, User, MapPin, Heart, Home, DollarSign, CreditCard, Building2, File, Edit, Save, X } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "../../integrations/supabase/client";
import { useToast } from "../../hooks/use-toast";

interface Parte {
  id: string;
  processo_id: string;
  tipo_parte: string;
  nome_completo: string;
  email?: string | null;
  telefone?: string | null;
  cpf_cnpj?: string | null;
  status_documentos: string;
  documentos?: any[] | null;
  dados_pessoais?: any;
  dados_conjuge?: any;
  dados_imovel?: any;
  endereco?: any;
  dados_bancarios?: any;
  created_at: string;
}

interface ParteDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  parte: Parte;
  onUpdate?: () => void;
}

type TabType = 'pessoais' | 'endereco' | 'conjuge' | 'imovel' | 'bancarios' | 'documentos';

const getTipoParteLabel = (tipo: string): string => {
  const labels: Record<string, string> = {
    locatario: 'Locatário',
    fiador: 'Fiador',
    proprietario: 'Proprietário',
    comprador: 'Comprador',
    vendedor: 'Vendedor',
  };
  return labels[tipo] || tipo;
};

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pendente: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    completo: 'bg-green-100 text-green-800 border-green-300',
    incompleto: 'bg-red-100 text-red-800 border-red-300',
  };
  return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
};

const formatCPFCNPJ = (value?: string | null): string => {
  if (!value) return 'Não informado';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
};

const formatCEP = (value?: string | null): string => {
  if (!value) return 'Não informado';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 8) {
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return value;
};

const formatTelefone = (value?: string | null): string => {
  if (!value) return 'Não informado';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
};

const formatData = (value?: string | null): string => {
  if (!value) return 'Não informado';
  try {
    const date = new Date(value);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return value;
  }
};

const getDocumentoIcon = (tipo: string) => {
  const icons: Record<string, any> = {
    rg: FileText,
    cpf: FileText,
    comprovante_endereco: Home,
    comprovante_renda: DollarSign,
    certidao_estado_civil: Heart,
    outros: File,
  };
  return icons[tipo] || File;
};

const getDocumentoLabel = (tipo: string): string => {
  const labels: Record<string, string> = {
    rg: 'RG',
    cpf: 'CPF',
    comprovante_endereco: 'Comprovante de Endereço',
    comprovante_renda: 'Comprovante de Renda',
    certidao_estado_civil: 'Certidão de Estado Civil',
    outros: 'Outros Documentos',
  };
  return labels[tipo] || tipo;
};

export function ParteDetailModal({ isOpen, onClose, parte, onUpdate }: ParteDetailModalProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('pessoais');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estado para edição
  const [editedData, setEditedData] = useState({
    nome_completo: parte.nome_completo || '',
    email: parte.email || '',
    telefone: parte.telefone || '',
    cpf_cnpj: parte.cpf_cnpj || '',
    dados_pessoais: parte.dados_pessoais || {},
    endereco: parte.endereco || {},
    dados_conjuge: parte.dados_conjuge || {},
    dados_imovel: parte.dados_imovel || {},
    dados_bancarios: parte.dados_bancarios || {},
  });

  // Sincronizar editedData quando parte mudar
  useEffect(() => {
    setEditedData({
      nome_completo: parte.nome_completo || '',
      email: parte.email || '',
      telefone: parte.telefone || '',
      cpf_cnpj: parte.cpf_cnpj || '',
      dados_pessoais: parte.dados_pessoais || {},
      endereco: parte.endereco || {},
      dados_conjuge: parte.dados_conjuge || {},
      dados_imovel: parte.dados_imovel || {},
      dados_bancarios: parte.dados_bancarios || {},
    });
  }, [parte]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('partes_processo')
        .update({
          nome_completo: editedData.nome_completo,
          email: editedData.email,
          telefone: editedData.telefone,
          cpf_cnpj: editedData.cpf_cnpj,
          dados_pessoais: editedData.dados_pessoais,
          endereco: editedData.endereco,
          dados_conjuge: editedData.dados_conjuge,
          dados_imovel: editedData.dados_imovel,
          dados_bancarios: editedData.dados_bancarios,
          updated_at: new Date().toISOString(),
        })
        .eq('id', parte.id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Dados da parte atualizados com sucesso',
      });

      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar os dados',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData({
      nome_completo: parte.nome_completo || '',
      email: parte.email || '',
      telefone: parte.telefone || '',
      cpf_cnpj: parte.cpf_cnpj || '',
      dados_pessoais: parte.dados_pessoais || {},
      endereco: parte.endereco || {},
      dados_conjuge: parte.dados_conjuge || {},
      dados_imovel: parte.dados_imovel || {},
      dados_bancarios: parte.dados_bancarios || {},
    });
    setIsEditing(false);
  };

  const getSignedUrl = async (rawUrl: string): Promise<string> => {
    try {
      const u = new URL(rawUrl);
      const parts = u.pathname.split('/').filter(Boolean);
      const objectIdx = parts.findIndex((p) => p === 'object');
      if (objectIdx < 0 || parts.length <= objectIdx + 3) return rawUrl;
      const bucket = parts[objectIdx + 2];
      const path = parts.slice(objectIdx + 3).join('/');
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 60 * 60);
      if (!error && data?.signedUrl) return data.signedUrl;
      return rawUrl;
    } catch {
      return rawUrl;
    }
  };

  const handleDownloadDocument = async (url: string, nome: string) => {
    try {
      const signed = await getSignedUrl(url);
      const response = await fetch(signed);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = nome;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
    }
  };

  const handleViewDocument = async (url: string) => {
    const signed = await getSignedUrl(url);
    window.open(signed, '_blank');
  };

  const tabs = [
    { id: 'pessoais' as TabType, label: 'Dados Pessoais', icon: User },
    { id: 'endereco' as TabType, label: 'Endereço', icon: MapPin },
    ...(parte.dados_conjuge?.nome ? [{ id: 'conjuge' as TabType, label: 'Cônjuge', icon: Heart }] : []),
    ...(parte.dados_imovel?.rua ? [{ id: 'imovel' as TabType, label: 'Imóvel', icon: Building2 }] : []),
    { id: 'bancarios' as TabType, label: 'Dados Bancários', icon: CreditCard },
    { id: 'documentos' as TabType, label: 'Documentos', icon: FileText },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">{parte.nome_completo}</DialogTitle>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline">{getTipoParteLabel(parte.tipo_parte)}</Badge>
                <Badge className={getStatusColor(parte.status_documentos)}>
                  {parte.status_documentos}
                </Badge>
              </div>
            </div>
            <div className="flex gap-2">
              {!isEditing ? (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              ) : (
                <>
                  <Button onClick={handleCancel} variant="outline" disabled={isSaving}>
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={isSaving}>
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-6 flex-1 overflow-hidden">
          {/* Sidebar com tabs */}
          <div className="w-48 flex-shrink-0 border-r pr-4 overflow-y-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Conteúdo */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'pessoais' && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados Pessoais</CardTitle>
                  <CardDescription>Informações pessoais do cadastro</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nome Completo</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.nome_completo}
                        onChange={(e) => setEditedData({ ...editedData, nome_completo: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.nome_completo || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">CPF/CNPJ</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.cpf_cnpj}
                        onChange={(e) => setEditedData({ ...editedData, cpf_cnpj: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{formatCPFCNPJ(editedData.cpf_cnpj)}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">RG</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.dados_pessoais?.rg || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, rg: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.dados_pessoais?.rg || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Data de Nascimento</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editedData.dados_pessoais?.data_nascimento || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, data_nascimento: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{formatData(editedData.dados_pessoais?.data_nascimento)}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editedData.email}
                        onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.email || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Telefone</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editedData.telefone}
                        onChange={(e) => setEditedData({ ...editedData, telefone: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{formatTelefone(editedData.telefone)}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estado Civil</label>
                    {isEditing ? (
                      <select
                        value={editedData.dados_pessoais?.estado_civil || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, estado_civil: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      >
                        <option value="">Selecione</option>
                        <option value="solteiro">Solteiro(a)</option>
                        <option value="casado">Casado(a)</option>
                        <option value="divorciado">Divorciado(a)</option>
                        <option value="viuvo">Viúvo(a)</option>
                        <option value="uniao_estavel">União Estável</option>
                      </select>
                    ) : (
                      <p className="text-sm mt-1">{editedData.dados_pessoais?.estado_civil || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Regime de Casamento</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.dados_pessoais?.regime_casamento || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, regime_casamento: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.dados_pessoais?.regime_casamento || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Renda Familiar</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.dados_pessoais?.renda_familiar || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, renda_familiar: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.dados_pessoais?.renda_familiar || 'Não informado'}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Tipo de Garantia</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.dados_pessoais?.garantia_locaticia || ''}
                        onChange={(e) => setEditedData({ 
                          ...editedData, 
                          dados_pessoais: { ...editedData.dados_pessoais, garantia_locaticia: e.target.value }
                        })}
                        className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{editedData.dados_pessoais?.garantia_locaticia || 'Não informado'}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'endereco' && (
              <Card>
                <CardHeader>
                  <CardTitle>Endereço</CardTitle>
                  <CardDescription>Endereço residencial</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Rua</label>
                    <p className="text-sm mt-1">{parte.endereco?.rua || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Número</label>
                    <p className="text-sm mt-1">{parte.endereco?.numero || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Complemento</label>
                    <p className="text-sm mt-1">{parte.endereco?.complemento || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Bairro</label>
                    <p className="text-sm mt-1">{parte.endereco?.bairro || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Cidade</label>
                    <p className="text-sm mt-1">{parte.endereco?.cidade || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estado</label>
                    <p className="text-sm mt-1">{parte.endereco?.estado || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">CEP</label>
                    <p className="text-sm mt-1">{formatCEP(parte.endereco?.cep)}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'conjuge' && parte.dados_conjuge?.nome && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados do Cônjuge</CardTitle>
                  <CardDescription>Informações do cônjuge</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Nome Completo</label>
                    <p className="text-sm mt-1">{parte.dados_conjuge?.nome || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">CPF</label>
                    <p className="text-sm mt-1">{formatCPFCNPJ(parte.dados_conjuge?.cpf)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">RG</label>
                    <p className="text-sm mt-1">{parte.dados_conjuge?.rg || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Data de Nascimento</label>
                    <p className="text-sm mt-1">{formatData(parte.dados_conjuge?.data_nascimento)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-sm mt-1">{parte.dados_conjuge?.email || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Telefone</label>
                    <p className="text-sm mt-1">{formatTelefone(parte.dados_conjuge?.telefone)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Renda</label>
                    <p className="text-sm mt-1">{parte.dados_conjuge?.renda || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estado Civil</label>
                    <p className="text-sm mt-1">{parte.dados_conjuge?.estado_civil || 'Não informado'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Endereço</label>
                    <p className="text-sm mt-1">
                      {parte.dados_conjuge?.rua ? 
                        `${parte.dados_conjuge.rua}, ${parte.dados_conjuge.numero || 'S/N'} ${parte.dados_conjuge.complemento ? '- ' + parte.dados_conjuge.complemento : ''} - ${parte.dados_conjuge.bairro}, ${parte.dados_conjuge.cidade}/${parte.dados_conjuge.estado} - CEP: ${formatCEP(parte.dados_conjuge.cep)}`
                        : 'Não informado'
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'imovel' && parte.dados_imovel?.rua && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados do Imóvel</CardTitle>
                  <CardDescription>Informações do imóvel</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Endereço</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.rua || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Número</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.numero || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Complemento</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.complemento || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Bairro</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.bairro || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Cidade</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.cidade || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estado</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.estado || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">CEP</label>
                    <p className="text-sm mt-1">{formatCEP(parte.dados_imovel?.cep)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Condomínio</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.condominio || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Vaga</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.vaga || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Matrícula</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.matricula || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Código Cartográfico</label>
                    <p className="text-sm mt-1">{parte.dados_imovel?.codigo_cartografico || 'Não informado'}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'bancarios' && (
              <Card>
                <CardHeader>
                  <CardTitle>Dados Bancários</CardTitle>
                  <CardDescription>Informações para pagamentos</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Banco</label>
                    <p className="text-sm mt-1">{parte.dados_bancarios?.banco || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Agência</label>
                    <p className="text-sm mt-1">{parte.dados_bancarios?.agencia || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Conta</label>
                    <p className="text-sm mt-1">{parte.dados_bancarios?.conta || 'Não informado'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Tipo de Conta</label>
                    <p className="text-sm mt-1">{parte.dados_bancarios?.tipo_conta || 'Não informado'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Chave PIX</label>
                    <p className="text-sm mt-1">{parte.dados_bancarios?.chave_pix || 'Não informado'}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'documentos' && (
              <Card>
                <CardHeader>
                  <CardTitle>Documentos Enviados</CardTitle>
                  <CardDescription>Arquivos anexados ao cadastro</CardDescription>
                </CardHeader>
                <CardContent>
                  {parte.documentos && parte.documentos.length > 0 ? (
                    <div className="space-y-3">
                      {parte.documentos.map((doc, index) => {
                        const Icon = getDocumentoIcon(doc.tipo);
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                <Icon className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{getDocumentoLabel(doc.tipo)}</p>
                                <p className="text-xs text-muted-foreground">{doc.nome}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDocument(doc.url)}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Visualizar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadDocument(doc.url, doc.nome)}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Baixar
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhum documento enviado</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
